import React, { FC, useMemo } from 'react';
import { Tabs, Spin } from 'antd';
import type { TabsProps } from 'antd';
import { IResourceComponentsProps, useNavigation } from '@refinedev/core';
import { useForm } from '@refinedev/antd';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InternalLayout } from '../../../layouts/InternalLayout';
import styles from './edit.module.scss';
import {
    ApplicationSummary,
    AttachmentsList,
    MedicationSummary,
} from './components';
import {
    ValidationResults,
    Expertises,
    Changes,
    DocumentHistory,
    StateHistory,
    Submissions,
} from '../../../components/applications';
import { formatDateLabel } from '../../../utils/format';
import { hasMeaningfulValue } from './utils';

const tabKey = {
    APPLICATION: 'application',
    VALIDATION: 'validation',
    EXPERTISE: 'expertise',
    CHANGES: 'changes',
    DOCUMENTS: 'documents',
    SUBMISSIONS: 'submissions',
    STATE_HISTORY: 'state_history',
};

type TimelineItem = {
    key: string;
    title: string;
    date?: string | null;
    isCurrent?: boolean;
};

export const ApplicationDossier: FC<IResourceComponentsProps> = () => {
    const { id } = useParams();
    const { replace } = useNavigation();
    const { t } = useTranslation();

    const { formLoading, queryResult } = useForm<any>({
        action: 'edit',
        resource: 'applications',
        id,
        mutationMode: 'pessimistic',
    });

    const dataSource = queryResult?.data?.data;
    const fileList = useMemo(() => {
        if (Array.isArray(dataSource?.files) && dataSource.files.length) {
            return dataSource.files as any[];
        }

        if (dataSource?.file) {
            return [dataSource.file];
        }

        return [];
    }, [dataSource]);

    const statusTimeline = useMemo<TimelineItem[]>(() => {
        const history = Array.isArray(dataSource?.state_history)
            ? dataSource.state_history
            : [];

        const normalizeTitle = (title: string) => title?.trim?.() ?? '';
        const hasMeaningfulTitle = (title: string) => {
            const clean = normalizeTitle(title);
            return (
                Boolean(clean) &&
                clean !== '-' &&
                clean !== '—' &&
                clean !== '–'
            );
        };

        const resolveCurrentStatus = () => {
            const statusField = dataSource?.status;

            let code: string | undefined;
            let name: string | undefined;

            if (typeof statusField === 'string') {
                code = statusField;
            } else if (statusField && typeof statusField === 'object') {
                code =
                    statusField.code ??
                    statusField.status_code ??
                    statusField.state_code ??
                    undefined;
                name =
                    statusField.name ??
                    statusField.status_name ??
                    statusField.state_name ??
                    undefined;
            }

            if (!code) {
                code = dataSource?.status_code ?? dataSource?.state_code;
            }

            if (!name) {
                name = dataSource?.status_name ?? dataSource?.state_name;
            }

            const translated = code
                ? t(`applications.fields.${code}`, name ?? code)
                : null;

            const title = name ?? translated ?? code ?? null;

            return title
                ? {
                      title,
                      date:
                          dataSource?.updated_at ??
                          dataSource?.status_updated_at ??
                          null,
                  }
                : null;
        };

        const currentStatus = resolveCurrentStatus();

        const currentItem = currentStatus
            ? [
                  {
                      key: 'current-status',
                      title: currentStatus.title,
                      date: currentStatus.date,
                      isCurrent: true,
                  },
              ]
            : [];

        const historyItems = history
            .map((entry: any, index: number) => ({
                key: String(index),
                title:
                    entry?.state?.name ??
                    entry?.state_name ??
                    entry?.status_name ??
                    '-',
                date:
                    entry?.changedOn ?? entry?.changed_on ?? entry?.created_at,
                isCurrent: false,
            }))
            .filter((item: TimelineItem) => {
                return hasMeaningfulTitle(item.title);
            });

        return [...currentItem, ...historyItems].filter(
            (item: TimelineItem) => {
                return hasMeaningfulTitle(item.title);
            }
        );
    }, [dataSource]);

    const applicationNumber =
        dataSource?.registration_certificate_number ||
        dataSource?.moh_application_number ||
        dataSource?.certificate?.number;

    const applicationDateRaw = [
        dataSource?.registration_certificate_date,
        dataSource?.moh_application_date,
    ].find(hasMeaningfulValue);

    const applicationDate = formatDateLabel(applicationDateRaw, '');
    const hasApplicationDate = hasMeaningfulValue(applicationDate);

    const summaryTitle = applicationNumber
        ? `№ ${applicationNumber}${
              hasApplicationDate
                  ? ` ${t(
                        'applications.wizard.application_date_suffix'
                    )} ${applicationDate}`
                  : ''
          }`
        : undefined;

    const tabItems = useMemo<TabsProps['items']>(() => {
        return [
            {
                key: tabKey.APPLICATION,
                label: t('applications.fields.application'),
                children: (
                    <div className={styles.dossierTabPane}>
                        <ApplicationSummary
                            data={dataSource}
                            heading={t('applications.fields.application')}
                        />
                        <MedicationSummary
                            data={dataSource?.medication}
                            heading={t(
                                'applications.wizard.medicine_information',
                                'Інформація про лікарський засіб'
                            )}
                        />
                        <AttachmentsList
                            files={fileList}
                            heading={t(
                                'applications.wizard.uploaded_files',
                                'Завантажені файли'
                            )}
                            className={styles.dossierAttachments}
                        />
                    </div>
                ),
            },
            {
                key: tabKey.VALIDATION,
                label: t('applications.titles.validation_results'),
                children: (
                    <ValidationResults
                        data={dataSource?.ectd_errors ?? []}
                        group="applications.titles"
                    />
                ),
            },
            {
                key: tabKey.EXPERTISE,
                label: t('applications.titles.expertises'),
                children: (
                    <Expertises
                        data={dataSource?.expertises ?? []}
                        group="applications.titles"
                    />
                ),
            },
            {
                key: tabKey.CHANGES,
                label: t('applications.titles.change_history'),
                children: (
                    <Changes
                        data={dataSource?.changes ?? []}
                        group="applications.titles"
                    />
                ),
            },
            {
                key: tabKey.STATE_HISTORY,
                label: t('applications.titles.state_history'),
                children: (
                    <StateHistory
                        data={dataSource?.state_history ?? []}
                        group="applications.titles"
                    />
                ),
            },
            {
                key: tabKey.DOCUMENTS,
                label: t('applications.titles.document_history'),
                children: (
                    <DocumentHistory
                        data={dataSource?.print_history ?? []}
                        group="applications.titles"
                    />
                ),
            },
            {
                key: tabKey.SUBMISSIONS,
                label: t('applications.titles.submissions'),
                children: (
                    <Submissions
                        submissions={dataSource?.submissions ?? []}
                        status={dataSource?.status}
                    />
                ),
            },
        ];
    }, [dataSource, fileList, t]);

    const sidebar = (
        <aside className={styles.statusWrapper}>
            <h3 className={styles.statusTitle}>
                {t('applications.wizard.status_timeline', 'Статус заяви')}
            </h3>
            <ul className={styles.timeline}>
                {statusTimeline.length ? (
                    statusTimeline.map(item => (
                        <li
                            key={item.key}
                            className={
                                item.isCurrent
                                    ? `${styles.timelineItem} ${styles.timelineItemCurrent}`
                                    : styles.timelineItem
                            }
                        >
                            {item.title}
                        </li>
                    ))
                ) : (
                    <li className={styles.timelineItem}>
                        {t('applications.titles.no_data')}
                    </li>
                )}
            </ul>
        </aside>
    );

    return (
        <InternalLayout
            onBack={() => replace('/applications')}
            title={t('applications.wizard.dossier_title', 'Досьє')}
            contentTitle={summaryTitle}
            sidebar={sidebar}
        >
            <Spin spinning={formLoading}>
                <Tabs
                    defaultActiveKey={tabKey.APPLICATION}
                    className={styles.dossierTabs}
                    items={tabItems}
                />
            </Spin>
        </InternalLayout>
    );
};

export default ApplicationDossier;
